import { normalizePunchListBandValue } from "./expertBandPresentation";

const AGREEMENT_WINDOW_CONTRACT = "reference-agreement-window@1";
const AGREEMENT_WINDOW_METHODOLOGY = "wider-of-observed-range-or-mean-plus-minus@1";

export const DIRECTION_CASE = Object.freeze({
  OUTSIDE_TO_INSIDE: "outside_to_inside",
  BOTH_INSIDE: "both_inside",
  OUTSIDE_CLOSER: "outside_closer",
  OUTSIDE_NOT_CLOSER: "outside_not_closer",
  INSIDE_TO_OUTSIDE: "inside_to_outside",
});

const finite = value => value === null || value === undefined || value === ""
  ? null
  : Number.isFinite(Number(value)) ? Number(value) : null;
const pointWord = value => value === 1 ? "point" : "points";

export function selectGovernedAgreementWindow(reference) {
  const window = reference?.agreementWindow;
  if (
    window?.contractVersion !== AGREEMENT_WINDOW_CONTRACT ||
    window?.methodology !== AGREEMENT_WINDOW_METHODOLOGY ||
    window?.halfWidth !== 10
  ) return null;
  const low = normalizePunchListBandValue(window.low);
  const high = normalizePunchListBandValue(window.high);
  return Number.isFinite(low) && Number.isFinite(high) && low <= high ? { low, high } : null;
}

const position = (rating, low, high) => rating < low
  ? { inside: false, side: "below", distance: low - rating }
  : rating > high
    ? { inside: false, side: "above", distance: rating - high }
    : { inside: true, side: "inside", distance: 0 };

export function classifyRepeatedJudgmentDirection({
  firstRating,
  secondRating,
  agreementWindowLow,
  agreementWindowHigh,
} = {}) {
  const first = finite(firstRating);
  const second = finite(secondRating);
  const low = finite(agreementWindowLow);
  const high = finite(agreementWindowHigh);
  if ([first, second, low, high].some(value => value === null) || low > high) return null;
  const firstPosition = position(first, low, high);
  const secondPosition = position(second, low, high);
  const numericDirection = second > first ? "higher" : second < first ? "lower" : "unchanged";
  let semanticCase;
  let distanceMovement = "unchanged";
  if (!firstPosition.inside && secondPosition.inside) {
    semanticCase = DIRECTION_CASE.OUTSIDE_TO_INSIDE;
    distanceMovement = "closer";
  }
  else if (firstPosition.inside && secondPosition.inside) semanticCase = DIRECTION_CASE.BOTH_INSIDE;
  else if (firstPosition.inside) {
    semanticCase = DIRECTION_CASE.INSIDE_TO_OUTSIDE;
    distanceMovement = "farther";
  }
  else if (secondPosition.distance < firstPosition.distance) {
    semanticCase = DIRECTION_CASE.OUTSIDE_CLOSER;
    distanceMovement = "closer";
  } else {
    semanticCase = DIRECTION_CASE.OUTSIDE_NOT_CLOSER;
    if (secondPosition.distance > firstPosition.distance) distanceMovement = "farther";
  }
  return {
    semanticCase,
    firstRating: first,
    secondRating: second,
    agreementWindow: { low, high },
    first: firstPosition,
    second: secondPosition,
    distanceMovement,
    distanceChange: Math.abs(secondPosition.distance - firstPosition.distance),
    numericDirection,
  };
}

export function projectRepeatedJudgmentDirections({ mirrorRows = [], referenceRows = [] } = {}) {
  if (!mirrorRows.length) return null;
  const rows = mirrorRows.map(mirror => {
    const referenceRow = referenceRows.find(candidate =>
      candidate.parameterId === mirror.parameterId ||
      candidate.sourceParameterLabel === mirror.parameter
    );
    const window = selectGovernedAgreementWindow(referenceRow?.reference);
    const direction = window && classifyRepeatedJudgmentDirection({
      firstRating: mirror.firstValue,
      secondRating: mirror.secondValue,
      agreementWindowLow: window.low,
      agreementWindowHigh: window.high,
    });
    return direction ? {
      parameterId: mirror.parameterId,
      parameter: mirror.parameter || referenceRow?.sourceParameterLabel || mirror.parameterId,
      ...direction,
    } : null;
  });
  if (rows.some(row => !row)) return null;
  const movedToward = rows.filter(row => [DIRECTION_CASE.OUTSIDE_TO_INSIDE, DIRECTION_CASE.OUTSIDE_CLOSER]
    .includes(row.semanticCase));
  const landedInside = rows.filter(row => row.second.inside);
  const movedAway = rows.filter(row => row.semanticCase === DIRECTION_CASE.INSIDE_TO_OUTSIDE ||
    (row.semanticCase === DIRECTION_CASE.OUTSIDE_NOT_CLOSER && row.distanceMovement === "farther"));
  const commonNumericDirection = rows.length === 4 && rows[0].numericDirection !== "unchanged" &&
    rows.every(row => row.numericDirection === rows[0].numericDirection)
    ? rows[0].numericDirection
    : null;
  return { rows, movedToward, landedInside, movedAway, commonNumericDirection };
}

export function repeatedJudgmentDirectionMessage(direction) {
  if (!direction) return null;
  const firstDistance = direction.first.distance;
  const secondDistance = direction.second.distance;
  const change = direction.distanceChange;
  if (direction.semanticCase === DIRECTION_CASE.OUTSIDE_TO_INSIDE) {
    return `Your first rating was ${firstDistance} ${pointWord(firstDistance)} outside the agreement window. This rating is inside it.`;
  }
  if (direction.semanticCase === DIRECTION_CASE.BOTH_INSIDE) {
    return "Both of your ratings are inside the agreement window.";
  }
  if (direction.semanticCase === DIRECTION_CASE.OUTSIDE_CLOSER) {
    return `Your second rating moved ${change} ${pointWord(change)} closer to the agreement window, though it is still ${secondDistance} ${pointWord(secondDistance)} outside it. This is one worth playing again with the window on screen.`;
  }
  if (direction.semanticCase === DIRECTION_CASE.INSIDE_TO_OUTSIDE) {
    return `Your first rating was inside the agreement window. Your second rating is ${secondDistance} ${pointWord(secondDistance)} outside it and moved away from the window. Play the voice again with the window on screen, then revisit the anchors for this quality before you move on.`;
  }
  if (direction.distanceMovement === "farther") {
    return `Your second rating moved ${change} ${pointWord(change)} farther from the agreement window and is now ${secondDistance} ${pointWord(secondDistance)} outside it. This is one worth playing again with the window on screen.`;
  }
  return `Both of your ratings are ${secondDistance} ${pointWord(secondDistance)} outside the agreement window. The second rating did not move closer. This is one worth playing again with the window on screen.`;
}

const joinedParameters = rows => rows.length === 1
  ? rows[0].parameter
  : `${rows.slice(0, -1).map(row => row.parameter).join(", ")} and ${rows.at(-1).parameter}`;

export function repeatedJudgmentSummaryMessages(projection) {
  if (!projection?.rows?.length) return [];
  const total = projection.rows.length;
  const toward = projection.movedToward.length;
  const inside = projection.landedInside.length;
  const messages = [total === 1
    ? `For this quality, the second rating ${toward ? "moved toward" : "did not move toward"} the agreement window and ${inside ? "landed" : "did not land"} inside it.`
    : toward === total && inside === total
    ? `On all ${total} of these qualities, your second rating moved toward the agreement window, and all ${total} landed inside it.`
    : `On ${toward} of these ${total} qualities, your second rating moved toward the agreement window, and on ${inside} it landed inside it.`];
  if (projection.commonNumericDirection) {
    messages.push(`All ${total} second ratings moved ${projection.commonNumericDirection} than the first ratings.`);
  }
  if (projection.movedAway.length) {
    const names = joinedParameters(projection.movedAway);
    messages.push(projection.movedAway.length === 1
      ? `${names} moved away from the agreement window this time, so that is the one worth another listen.`
      : `${names} moved away from the agreement window this time, so those are the ${projection.movedAway.length} worth another listen.`);
  }
  messages.push("Exact number matching is not the goal; the relevant comparison is whether the second rating moved toward the experienced-listener agreement window.");
  return messages;
}
